import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Divider, List } from '@material-ui/core';
import ContactRow from './components/ContactRow';
import Contact, { CreateContactOptions, LegacyContact } from 'src/interfaces/api/Contact';
import RevokeAccessDialog from './components/RevokeAccessDialog';
import MigrateContactDialog from './components/MigrateContactDialog';
import SearchBar from './components/SearchBar';

interface Props {
  onMigrateContact: (contactOptions: CreateContactOptions) => void;
  onRevokeCustomerAccess: (contactId: string) => void;
  contacts: LegacyContact[];
  contactsV2: Contact[];
  siteId?: string;
  loading: boolean;
}

const ContactsList: FC<Props> = ({
  onMigrateContact,
  onRevokeCustomerAccess,
  siteId,
  contactsV2,
  contacts,
  loading,
}) => {
  const [legacyContactToMigrate, setLegacyContactToMigrate] = useState<LegacyContact | undefined>();
  const [contactToRevokeAccess, setContactToRevokeAccess] = useState<Contact | undefined>();
  const [fullnameQuery, setFullnameQuery] = useState<string>('');
  const [newContacts, setNewContacts] = useState<Contact[]>([]);

  useEffect(() => {
    const oldContactIds = contacts.map((contact) => contact.id);
    const ids = contactsV2.filter((contact) => !oldContactIds.includes(contact.legacyContactId || -1));
    setNewContacts(ids);
  }, [contacts, contactsV2]);

  const getContactV2WithLegacyId = useCallback(
    (legacyId: number) => {
      return contactsV2.find((contactV2) => contactV2.legacyContactId === legacyId);
    },
    [contactsV2]
  );

  const handleMigrateContact = async (contactOptions: CreateContactOptions) => {
    try {
      await onMigrateContact(contactOptions);
      setLegacyContactToMigrate(undefined);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const handleRevokeAccess = async () => {
    try {
      if (contactToRevokeAccess?.customerAccessUserId) {
        await onRevokeCustomerAccess(contactToRevokeAccess.customerAccessUserId);
      }
      setContactToRevokeAccess(undefined);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const filteredLegacyContacts = useCallback(() => {
    return contacts.filter(
      (contact) =>
        -1 !== `${contact.name || ''} ${contact.lastname || ''}`.toLowerCase().indexOf(fullnameQuery.toLowerCase())
    );
  }, [contacts, fullnameQuery]);

  const filteredContacts = useCallback(() => {
    return newContacts.filter(
      (contact) =>
        -1 !== `${contact.firstname || ''} ${contact.lastname || ''}`.toLowerCase().indexOf(fullnameQuery.toLowerCase())
    );
  }, [newContacts, fullnameQuery]);

  return (
    <>
      <List>
        {!loading ? (
          <Fragment>
            <SearchBar onChange={setFullnameQuery} value={fullnameQuery} />

            {filteredLegacyContacts().map((contact, key) => {
              const contactV2 = getContactV2WithLegacyId(contact.id);

              return (
                <Fragment key={key}>
                  {key !== 0 && <Divider component='div' />}

                  <ContactRow
                    onAskContactAccess={() => setLegacyContactToMigrate(contact)}
                    onRevokeContactAccess={() => setContactToRevokeAccess(contactV2)}
                    contactV2={contactV2}
                    contact={contact}
                    siteId={siteId}
                  />
                </Fragment>
              );
            })}

            {filteredContacts().map((contact, key) => (
              <Fragment key={key}>
                {(key !== 0 || contacts.length > 0) && <Divider component='div' />}

                <ContactRow
                  onRevokeContactAccess={() => setContactToRevokeAccess(contact)}
                  contactV2={contact}
                  siteId={siteId}
                />
              </Fragment>
            ))}
          </Fragment>
        ) : (
          <li>Chargement...</li>
        )}
      </List>

      {legacyContactToMigrate && (
        <MigrateContactDialog
          siteId={siteId!}
          open={true}
          onValidate={handleMigrateContact}
          onClose={() => setLegacyContactToMigrate(undefined)}
          legacyContact={legacyContactToMigrate}
        />
      )}

      {contactToRevokeAccess && (
        <RevokeAccessDialog
          open={true}
          onValidate={handleRevokeAccess}
          onClose={() => setContactToRevokeAccess(undefined)}
          contact={contactToRevokeAccess}
        />
      )}
    </>
  );
};

export default ContactsList;
